import { getLabel, getRichLabel } from "@/_services/lists"
import { getLocale, loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { formatCurrency, formatDate, formatDateTime, personRoles } from "@/_services/utils"
import { invoiceSearchFields } from "@/billing/InvoicesPage"
import { amendmentSearchFields } from "@/financing/AmendmentsPage"
import { creditLineSearchFields } from "@/financing/CreditLinesPage"
import { committeeSearchFields } from "@/support/CommitteesPage"
import { getUserSearchFields } from "@/user/UsersPage"
import axios from "axios"
import {
  formatAddress,
  formatBytes,
  formatMilliseconds,
  formatPercentage,
  frontScripts,
  getEntityClientRoute,
  getEntityServerRoute,
  getEntityStatusListName,
  getFilterFunction,
} from "basikon-common-utils"
import get from "lodash.get"
import set from "lodash/set"

export function getEntitySearchFields(entityName) {
  if (!entityName) return

  // TODO: Move searchFields columns to their entities page when possible (if they don't use EntitiesPage)

  const contractLotSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 8 }, regex: true },
    ],
    [
      { field: "type", type: "multiple", label: loc`Type`, select: "contractLotType", colProps: { xs: 12, md: 4 } },
      { field: "status", type: "multiple", label: loc`Status`, select: "contractLotStatus", colProps: { xs: 12, md: 8 } },
    ],
    [
      { field: "externalReference", colProps: { xs: 12, md: 6 } },
      { field: "internalReference", colProps: { xs: 12, md: 6 } },
    ],
    [
      { field: "contractRegistration", label: "Contract", searchEntityName: "Contract", actionHidden: true, colProps: { xs: 12, md: 6 } },
      { field: "fundingRegistration", label: "Funding", searchEntityName: "Funding", actionHidden: true, colProps: { xs: 12, md: 6 } },
    ],
  ]

  const aiConversationSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 6 } },
      { field: "parentConversationRegistration", label: "Parent registration", colProps: { xs: 12, sm: 6 } },
      { field: "aiAgentRegistration", label: "Agent", colProps: { xs: 12, sm: 6 } },
      {
        label: "Person",
        field: "personRegistration",
        placeholder: "Type to search for a person",
        searchEntityName: "Person",
        actionHidden: true,
        colProps: { xs: 12, sm: 6 },
      },
      { field: "status", select: "aiConversationStatus", colProps: { xs: 12, sm: 6 } },
      { field: "type", select: "aiConversationType", colProps: { xs: 12, sm: 6 } },
      { field: "code", select: "aiConversationCode", colProps: { xs: 12, sm: 6 } },
      { field: "mode", select: "aiConversationMode", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const aiMemorySearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 6 } },
      { field: "status", select: "aiMemoryStatus", colProps: { xs: 12, sm: 6 } },
      { field: "type", select: "aiMemoryType", colProps: { xs: 12, sm: 6 } },
      {
        label: "Person",
        field: "personRegistration",
        placeholder: "Type to search for a person",
        searchEntityName: "Person",
        actionHidden: true,
        colProps: { xs: 12, sm: 6 },
      },
      { field: "username", label: "User", colProps: { xs: 12, sm: 6 } },
      { field: "aiAgentRegistration", label: "Agent", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const covenantSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 6 } },
      { field: "status", select: "covenantStatus", colProps: { xs: 12, sm: 6 } },
      { field: "type", select: "covenantType", colProps: { xs: 12, sm: 6 } },
      { field: "code", colProps: { xs: 12, sm: 6 } },
      {
        field: "orgaRegistration",
        label: "Organization",
        searchEntityName: "Person",
        query: { type: "O" },
        colProps: { xs: 12, md: 6 },
      },
    ],
    [
      { field: "fromStartDate", label: loc`From date`, mustBeLessThan: "toStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toStartDate", label: loc`To date`, mustBeGreaterThan: "fromStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "fromEndDate", label: loc`From end date`, mustBeLessThan: "toEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toEndDate", label: loc`To end date`, mustBeGreaterThan: "fromEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const counterSearchFields = [[{ field: "name", colProps: { xs: 12, sm: 8 }, regex: true }]]

  const contractSearchFields = [
    [
      { field: "type", type: "multiple", select: "contractType", colProps: { xs: 12, sm: 3 } },
      { field: "contractType", type: "multiple", select: "contractType", colProps: { xs: 12, sm: 3 }, hidden: true },
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "name", colProps: { xs: 12, sm: 6 }, regex: true },
    ],
    [
      { field: "status", type: "multiple", select: "contractStatus", colProps: { xs: 12, sm: 3 } },
      { field: "managementType", select: "managementType", type: "multiple", colProps: { xs: 12, sm: 3 } },
      { field: "purpose", select: "purpose", type: "multiple", colProps: { xs: 12, sm: 3 } },
      { field: "subPurpose", select: "subPurpose", type: "multiple", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "internalReference", colProps: { xs: 12, sm: 3 } },
      { field: "externalReference", colProps: { xs: 12, sm: 3 } },
    ],
    [{ legend: "Persons", field: "personsTitle" }], // Keep "field" to be able to hide legend by config
    [
      {
        field: "client",
        placeholder: "Type to search for a client",
        searchEntityName: "Person",
        query: { role: personRoles.CLIENT },
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "partner",
        placeholder: "Type to search for a partner",
        searchEntityName: "Person",
        query: { role: personRoles.PARTNER },
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "sales",
        placeholder: "Type to search for a sales",
        searchEntityName: "Person",
        query: { role: personRoles.SALES },
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
    ],
    [{ legend: "Assets", field: "assetsTitle" }], // Keep "field" to be able to hide legend by config
    [
      {
        field: "assetRegistration",
        placeholder: "Type to search for an asset",
        searchEntityName: "Asset",
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
    ],
    [{ legend: "Quotation", field: "quotationTitle" }], // Keep "field" to be able to hide legend by config
    [
      { field: "financialProduct", type: "multiple", select: "financialProduct", colProps: { xs: 12, sm: 3 } },
      { field: "financialScheme", type: "multiple", select: "financialScheme", colProps: { xs: 12, sm: 3 } },
      { field: "frequency", select: "frequency", type: "multiple", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "minDuration", label: loc`Duration (min)`, type: "number", mustBeLessThan: "maxDuration", colProps: { xs: 12, sm: 3 } },
      { field: "maxDuration", label: loc`Duration (max)`, type: "number", mustBeGreaterThan: "minDuration", colProps: { xs: 12, sm: 3 } },
      {
        field: "minFinancedAmountExclTax",
        label: loc`Financed amount (min)`,
        type: "currency",
        mustBeLessThan: "maxFinancedAmountExclTax",
        colProps: { xs: 12, sm: 3 },
      },
      {
        field: "maxFinancedAmountExclTax",
        label: loc`Financed amount (max)`,
        type: "currency",
        mustBeGreaterThan: "minFinancedAmountExclTax",
        colProps: { xs: 12, sm: 3 },
      },
    ],
    [
      { field: "fromStartDate", label: loc`From date`, mustBeLessThan: "toStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toStartDate", label: loc`To date`, mustBeGreaterThan: "fromStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "fromEndDate", label: loc`From end date`, mustBeLessThan: "toEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toEndDate", label: loc`To end date`, mustBeGreaterThan: "fromEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const dealSearchFields = [
    [
      { field: "type", type: "multiple", select: "contractType", colProps: { xs: 12, sm: 3 } },
      { field: "contractType", type: "multiple", select: "contractType", colProps: { xs: 12, sm: 3 }, hidden: true },
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "name", colProps: { xs: 12, sm: 6 }, regex: true },
    ],
    [
      { field: "status", type: "multiple", select: "dealStatus", colProps: { xs: 12, sm: 3 } },
      { field: "managementType", select: "managementType", type: "multiple", colProps: { xs: 12, sm: 3 } },
      { field: "purpose", select: "purpose", type: "multiple", colProps: { xs: 12, sm: 3 } },
      { field: "subPurpose", select: "subPurpose", type: "multiple", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "internalReference", colProps: { xs: 12, sm: 3 } },
      { field: "externalReference", colProps: { xs: 12, sm: 3 } },
    ],
    [{ legend: "Persons", field: "personsTitle" }], // Keep "field" to be able to hide legend by config
    [
      {
        field: "client",
        placeholder: "Type to search for a client",
        searchEntityName: "Prospect",
        query: { role: personRoles.CLIENT },
        multiple: true,
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "partner",
        placeholder: "Type to search for a partner",
        searchEntityName: "Person",
        query: { role: personRoles.PARTNER },
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "sales",
        placeholder: "Type to search for a sales",
        searchEntityName: "Person",
        query: { role: personRoles.SALES },
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
    ],
    [{ legend: "Assets", field: "assetsTitle" }], // Keep "field" to be able to hide legend by config
    [
      {
        field: "assetRegistration",
        placeholder: "Type to search for an asset",
        searchEntityName: "Asset",
        actionHidden: true,
        colProps: { xs: 12, sm: 4 },
      },
    ],
    [{ legend: "Quotation", field: "quotationTitle" }], // Keep "field" to be able to hide legend by config
    [
      { field: "financialProduct", type: "multiple", select: "financialProduct", colProps: { xs: 12, sm: 3 } },
      { field: "financialScheme", type: "multiple", select: "financialScheme", colProps: { xs: 12, sm: 3 } },
      { field: "frequency", select: "frequency", type: "multiple", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "minDuration", label: loc`Duration (min)`, mustBeLessThan: "maxDuration", type: "number", colProps: { xs: 12, sm: 3 } },
      { field: "maxDuration", label: loc`Duration (max)`, mustBeGreaterThan: "minDuration", type: "number", colProps: { xs: 12, sm: 3 } },
      {
        field: "minFinancedAmountExclTax",
        label: loc`Financed amount (min)`,
        type: "currency",
        mustBeLessThan: "maxFinancedAmountExclTax",
        colProps: { xs: 12, sm: 3 },
      },
      {
        field: "maxFinancedAmountExclTax",
        label: loc`Financed amount (max)`,
        type: "currency",
        mustBeGreaterThan: "minFinancedAmountExclTax",
        colProps: { xs: 12, sm: 3 },
      },
    ],
    [
      { field: "fromStartDate", label: loc`From date`, mustBeLessThan: "toStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toStartDate", label: loc`To date`, mustBeGreaterThan: "fromStartDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "fromEndDate", label: loc`From end date`, mustBeLessThan: "toEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "toEndDate", label: loc`To end date`, mustBeGreaterThan: "fromEndDate", type: "date", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const documentSearchFields = [
    [
      { field: "status", type: "multiple", select: "documentStatus", colProps: { xs: 12, sm: 3 } },
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "name", colProps: { xs: 12, sm: 6 }, regex: true },
    ],
    [
      { field: "entityName", type: "multiple", select: `/api/script/runs/${frontScripts.documentEntitiesWithWorkflow}`, colProps: { xs: 12, sm: 3 } },
      { field: "entityRegistration", colProps: { xs: 12, sm: 3 } },
      {
        field: "type",
        type: "multiple",
        select: `/api/script/runs/${frontScripts.documentTypesWithWorkflow}?entityNames={entityName}`,
        colProps: { xs: 12, sm: 6 },
      },
    ],
    [
      { field: "deliveryId", colProps: { xs: 12, sm: 3 } },
      { field: "deliveryStatus", type: "multiple", select: "documentDeliveryStatus", colProps: { xs: 12, sm: 3 } },
      { field: "externalReference", colProps: { xs: 12, sm: 3 } },
      { field: "externalGroupId", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const agreementSearchFields = [
    [
      { field: "trigram", colProps: { xs: 12, md: 4 } },
      { field: "name", colProps: { xs: 12, md: 8 } },
    ],
    [
      {
        field: "partnerRegistration",
        label: "Partner",
        searchEntityName: "Person",
        placeholder: "Type to search for a partner",
        query: { role: [personRoles.PARTNER, personRoles.BUSINESSUNIT, personRoles.AGENCY] },
        actionHidden: true,
        defaultOptions: true,
        colProps: { xs: 12, md: 4 },
      },
      { field: "type", type: "multiple", select: "agreementType", colProps: { xs: 12, md: 8 } },
    ],
    [
      { field: "status", select: "agreementStatus", type: "multiple", colProps: { xs: 12, md: 4 } },
      { field: "startDate", type: "date", mustBeLessThan: "endDate", colProps: { xs: 12, md: 4 } },
      { field: "endDate", type: "date", mustBeGreaterThan: "startDate", colProps: { xs: 12, md: 4 } },
    ],
  ]

  const assetSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 8 }, regex: true },
    ],
    [
      { field: "status", type: "multiple", label: loc`Status`, select: "assetStatus", colProps: { xs: 12, sm: 4 } },
      { field: "type", type: "multiple", label: loc`Type`, select: "assetType", colProps: { xs: 12, sm: 8 } },
    ],
    [
      { field: "registrationBookNumber" },
      { field: "minPriceExclTax", type: "currency", label: loc`Price (min)` },
      { field: "maxPriceExclTax", type: "currency", label: loc`Price (max)` },
    ],
    [{ legend: "Supplier", field: "supplierTitle" }], // Keep "field" to be able to hide legend by config
    [
      {
        field: "supplierRegistration",
        searchEntityName: "Person",
        placeholder: "Type to search for a supplier",
        query: { type: "C", role: personRoles.SUPPLIER },
      },
      { field: "invoiceNumber" },
      { field: "supplierCurrency", select: "currency" },
      { field: "deliveryDate", type: "date" },
      { field: "invoiceDate", type: "date" },
    ],
  ]

  const sepaSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "person", colProps: { xs: 12, sm: 8 }, regex: true },
    ],
    [
      { field: "status", type: "multiple", label: loc`Status`, select: "sepaStatus", colProps: { xs: 12, sm: 4 } },
      { field: "bank", colProps: { xs: 12, sm: 4 }, regex: true },
      { field: "method", select: "paymentMode", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const postingSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 3 } },
      { field: "type", colProps: { xs: 12, md: 3 }, select: "postingType" },
      { field: "dc", colProps: { xs: 12, md: 3 }, select: "postingDc", label: "Debit/Credit" },
    ],
    [
      {
        field: "contract",
        searchEntityName: "Contract",
        placeholder: "Type to search for a contract",
        actionHidden: true,
        regex: true,
        colProps: { xs: 12, md: 4 },
      },
      { field: "code", colProps: { xs: 12, md: 8 }, type: "multiple", select: "postingCode" },
    ],
    [
      {
        field: "person",
        searchEntityName: "Person",
        placeholder: "Type to search for a person",
        actionHidden: true,
        colProps: { xs: 12, md: 4 },
        regex: true,
      },
      { field: "status", type: "multiple", select: "postingStatus", colProps: { xs: 12, md: 8 } },
    ],
    [
      {
        field: "invoice",
        searchEntityName: "Invoice",
        placeholder: "Type to search for an invoice",
        actionHidden: true,
        colProps: { xs: 12, md: 4 },
        regex: true,
      },
      { field: "account", type: "multiple", select: "ledgerAccount", colProps: { xs: 12, md: 8 } },
    ],
  ]

  const postingEventSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 6 } },
      { field: "type", colProps: { xs: 12, md: 3 }, select: "postingEventType" },
    ],
    [
      {
        field: "contract",
        searchEntityName: "Contract",
        placeholder: "Type to search for a contract",
        actionHidden: true,
        regex: true,
        colProps: { xs: 12, md: 4 },
      },
      { field: "code", colProps: { xs: 12, md: 8 }, type: "multiple", select: "postingEventCode" },
    ],
    [
      {
        field: "person",
        searchEntityName: "Person",
        placeholder: "Type to search for a person",
        actionHidden: true,
        colProps: { xs: 12, md: 4 },
        regex: true,
      },
      { field: "status", type: "multiple", select: "postingEventStatus", colProps: { xs: 12, md: 8 } },
    ],
    [
      {
        field: "invoice",
        searchEntityName: "Invoice",
        placeholder: "Type to search for an invoice",
        actionHidden: true,
        colProps: { xs: 12, md: 4 },
        regex: true,
      },
      { field: "account", type: "multiple", select: "ledgerAccount", colProps: { xs: 12, md: 8 } },
    ],
  ]

  const productSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 4 }, regex: true },
      { field: "status", type: "multiple", select: "productStatus", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const assetLotSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "type", colProps: { xs: 12, md: 3 }, select: "assetLotType" },
      { field: "status", colProps: { xs: 12, md: 6 }, select: "assetLotStatus", multiple: true },
      { field: "internalReference", colProps: { xs: 12, md: 3 } },
      { field: "externalReference", colProps: { xs: 12, md: 3 } },
      { field: "owner", searchEntityName: "Person", query: { role: personRoles.OWNER }, colProps: { xs: 12, md: 4 } },
      { field: "supplier", searchEntityName: "Person", query: { role: personRoles.SUPPLIER }, colProps: { xs: 12, md: 4 } },
      { field: "transporter", searchEntityName: "Person", query: { role: "TRANSPORTER" }, colProps: { xs: 12, md: 4 } },
      {
        field: "minAmountExclTax",
        label: "Min. amount (excl. tax)",
        type: "currency",
        mustBeLessThan: "maxAmountExclTax",
        colProps: { xs: 12, md: 3 },
      },
      {
        field: "maxAmountExclTax",
        label: "Max. amount (excl. tax)",
        type: "currency",
        mustBeGreaterThan: "minAmountExclTax",
        colProps: { xs: 12, md: 3 },
      },
      {
        field: "minAmountInclTax",
        label: "Min. amount (incl. tax)",
        type: "currency",
        mustBeLessThan: "maxAmountInclTax",
        colProps: { xs: 12, md: 3 },
      },
      {
        field: "maxAmountInclTax",
        label: "Max. amount (incl. tax)",
        type: "currency",
        mustBeGreaterThan: "minAmountInclTax",
        colProps: { xs: 12, md: 3 },
      },
    ],
  ]

  const assetCatalogItemSearchFields = [
    [
      { field: "type", colProps: { xs: 12, md: 3 }, select: "assetCatalogItemType" },
      { field: "code", colProps: { xs: 12, md: 3 }, select: "assetCatalogItemCode", creatable: true },
      { field: "name", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "status", select: "assetCatalogItemStatus", multiple: true, colProps: { xs: 12, md: 6 } },
      { field: "catalogName", select: "assetCatalogItemCatalog", colProps: { xs: 12, md: 6 } },
      { field: "startDate", type: "date", mustBeLessThan: "endDate", colProps: { xs: 12, md: 6 } },
      { field: "endDate", type: "date", mustBeGreaterThan: "startDate", colProps: { xs: 12, md: 6 } },
      { field: "date", colProps: { xs: 12, md: 4 }, type: "date" },
      { field: "internalReference", colProps: { xs: 12, md: 4 } },
      { field: "externalReference", colProps: { xs: 12, md: 4 } },
    ],
  ]

  const campaignSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 6 } },
      { field: "name", colProps: { xs: 12, md: 6 } },
    ],
    [
      { field: "status", select: "campaignStatus", multiple: true, colProps: { xs: 12, md: 6 } },
      { field: "type", select: "campaignType", multiple: true, colProps: { xs: 12, md: 6 } },
    ],
  ]

  const taskSearchFields = [
    [
      { field: "subject", regex: true, colProps: { xs: 12, md: 8 } },
      { field: "project" },
      { field: "type", select: "taskType" },
      { field: "profiles", label: "Profiles", type: "multiple", select: "userProfile" },
      { field: "priority", select: "taskPriority" },
      { field: "ownerUsername", select: "/api/core/tasks/userNames?listMode=true" },
      { field: "fromStartDate", type: "date", mustBeLessThan: "toEndDate", path: "startDate" },
      { field: "toEndDate", type: "date", mustBeGreaterThan: "fromStartDate", path: "endDate" },
      { field: "fromDueDate", type: "date", mustBeLessThan: "toDueDate", path: "dueDate" },
      { field: "toDueDate", type: "date", mustBeGreaterThan: "fromDueDate", path: "dueDate" },
      { field: "done", type: "checkbox" },
    ],
  ]

  const projectSearchFields = [
    [
      { field: "registration", searchEntityName: "Project", colProps: { xs: 12, md: 6, lg: 3 } },
      { field: "name", colProps: { xs: 12, md: 6, lg: 3 }, regex: true },
      { field: "status", colProps: { xs: 12, md: 6, lg: 3 }, select: "projectStatus", multiple: true },
      { field: "type", colProps: { xs: 12, md: 6, lg: 3 }, select: "projectType", multiple: true },
    ],
    [
      { field: "client", colProps: { xs: 12, md: 6, lg: 3 }, searchEntityName: "Person", query: { role: personRoles.CLIENT } },
      { field: "ownerRegistration", colProps: { xs: 12, md: 6, lg: 3 }, searchEntityName: "Person" },
      { field: "clientOwnerRegistration", colProps: { xs: 12, md: 6, lg: 3 }, searchEntityName: "Person" },
      {
        field: "orgaRegistration",
        label: "Organization",
        searchEntityName: "Person",
        query: { type: "O" },
        colProps: { xs: 12, md: 6, lg: 3 },
      },
    ],
    [{ field: "addressLine", placeholder: "Type project location", regex: true, colProps: { xs: 12 } }],
    [
      { field: "startDate", colProps: { xs: 12, md: 6 }, type: "date", mustBeLessThan: "endDate" },
      { field: "endDate", colProps: { xs: 12, md: 6 }, type: "date", mustBeGreaterThan: "startDate" },
    ],
  ]

  const caseSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 4 } },
      {
        field: "contract",
        searchEntityName: "Contract",
        placeholder: "Type to search for a contract",
        actionHidden: true,
        regex: true,
        colProps: { xs: 12, sm: 8 },
      },
      { field: "status", type: "multiple", select: "caseStatus", colProps: { xs: 12, md: 4 } },
      { field: "person", label: "Person registration", colProps: { xs: 12, md: 8 }, regex: true },
      { field: "score", colProps: { xs: 12, md: 4 } },
      { field: "type", type: "multiple", select: "caseType", colProps: { xs: 12, md: 8 } },
      { field: "currency", type: "multiple", select: "currency", colProps: { xs: 12, md: 4 } },
      { field: "supplier", searchEntityName: "Person", query: { role: personRoles.SUPPLIER }, colProps: { xs: 12, md: 6 } },
      { field: "transporter", searchEntityName: "Person", query: { role: "TRANSPORTER" }, colProps: { xs: 12, md: 6 } },
      { field: "fromDate", label: loc`From date`, type: "date", mustBeLessThan: "toDate", colProps: { xs: 12, sm: 6 } },
      { field: "toDate", label: loc`To date`, type: "date", mustBeGreaterThan: "fromDate", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const scriptLogSearchFields = [
    [
      { field: "scriptName", colProps: { xs: 12, md: 6 } },
      { field: "username", colProps: { xs: 12, md: 6 } },
      { field: "requestId", colProps: { xs: 12, md: 6 } },
      { field: "level", colProps: { xs: 12, md: 6 }, select: "scriptLogLevel" },
    ],
  ]

  const personSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 5, md: 6 }, regex: true },
      { field: "birthDate", colProps: { xs: 12, sm: 3, md: 2 }, type: "date" },
    ],
    [
      { field: "type", type: "multiple", select: "personType", colProps: { xs: 12, sm: 4 } },
      { field: "address", colProps: { xs: 12, sm: 8 } },
    ],
    [
      { field: "role", type: "multiple", select: "personRole", colProps: { xs: 12, sm: 4 } },
      { field: "status", type: "multiple", select: "personStatus", colProps: { xs: 12, sm: 4 } },
      { field: "nationality", type: "multiple", select: "country", colProps: { xs: 12, sm: 4 } },
    ],
    [
      { field: "legalForm", select: "legalForm", colProps: { xs: 12, sm: 4 } },
      { field: "activityCode", select: "activityCode", colProps: { xs: 12, sm: 4 } },
    ],
    [
      { field: "accountingReference", colProps: { xs: 12, sm: 4 } },
      { field: "internalReference", colProps: { xs: 12, sm: 4 } },
      { field: "externalReference", colProps: { xs: 12, sm: 4 } },
    ],
    [
      { field: "mobile", type: "phone", colProps: { xs: 12, sm: 4 } },
      { field: "phone", type: "phone", colProps: { xs: 12, sm: 4 } },
      { field: "email", colProps: { xs: 12, sm: 4 }, regex: { fromStart: true, honorCase: true } },
    ],
  ]

  const communicationSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 6 } },
      { field: "subject", regex: true, colProps: { xs: 12, sm: 6 } },
      { field: "status", type: "multiple", select: "communicationStatus", colProps: { xs: 12, sm: 6 } },
      { field: "type", type: "multiple", select: "communicationType", colProps: { xs: 12, sm: 6 } },
      { field: "category", type: "multiple", select: "communicationCategory", colProps: { xs: 12, sm: 6 } },
      { field: "person", colProps: { xs: 12, md: 6 }, searchEntityName: "Person" },
      { field: "orgaRegistration", label: "Organization", searchEntityName: "Person", query: { type: "O" }, colProps: { xs: 12, md: 6 } },
      {
        field: "contract",
        searchEntityName: "Contract",
        placeholder: "Type to search for a contract",
        regex: true,
        colProps: { xs: 12, sm: 6 },
      },
      {
        field: "project",
        searchEntityName: "Project",
        placeholder: "Type to search for a project",
        regex: true,
        colProps: { xs: 12, sm: 6 },
      },
      {
        field: "invoice",
        searchEntityName: "Invoice",
        placeholder: "Type to search for an invoice",
        regex: true,
        colProps: { xs: 12, sm: 6 },
      },
      { field: "fromDate", label: loc`From date`, type: "date", mustBeLessThan: "toDate", colProps: { xs: 12, sm: 6 } },
      { field: "toDate", label: loc`To date`, type: "date", mustBeGreaterThan: "fromDate", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const claimSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "status", type: "multiple", select: "claimStatus", colProps: { xs: 12, md: 3 } },
      { field: "code", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 3 }, regex: true },
    ],
    [
      { field: "internalReference", colProps: { xs: 12, md: 6 } },
      { field: "externalReference", colProps: { xs: 12, md: 6 } },
    ],
    [
      { field: "startDate", label: loc`Start date`, type: "date", mustBeLessThan: "endDate", colProps: { xs: 12, sm: 6 } },
      { field: "endDate", label: loc`End date`, type: "date", mustBeGreaterThan: "startDate", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const fineSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "status", type: "multiple", select: "fineStatus", colProps: { xs: 12, md: 3 } },
      { field: "type", type: "multiple", select: "fineType", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 3 }, regex: true },
    ],
    [
      {
        field: "contract",
        searchEntityName: "Contract",
        placeholder: "Type to search for a contract",
        regex: true,
        colProps: { xs: 12, sm: 6 },
      },
      {
        field: "asset",
        searchEntityName: "Asset",
        placeholder: "Type to search for an asset",
        regex: true,
        colProps: { xs: 12, sm: 6 },
      },
    ],
    [
      { field: "issueDate", label: loc`Issue date`, type: "date", mustBeLessThan: "receptionDate", colProps: { xs: 12, sm: 6 } },
      { field: "receptionDate", label: loc`Reception date`, type: "date", mustBeGreaterThan: "issueDate", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const maintenanceSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "status", type: "multiple", select: "maintenanceStatus", colProps: { xs: 12, sm: 4 } },
      { field: "type", type: "multiple", select: "maintenanceType", colProps: { xs: 12, sm: 4 } },
    ],
    [
      { field: "supplier", colProps: { xs: 12, md: 6 }, searchEntityName: "Person" },
      { field: "orgaRegistration", label: "Organization", searchEntityName: "Person", query: { type: "O" }, colProps: { xs: 12, md: 6 } },
    ],
    [
      { field: "submitDate", label: loc`Submit date`, type: "date", colProps: { xs: 12, sm: 4 } },
      { field: "returnDate", label: loc`Return date`, type: "date", colProps: { xs: 12, sm: 4 } },
      { field: "validationDate", label: loc`Validation date`, type: "date", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const depreciationScheduleSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "status", type: "multiple", select: "depreciationScheduleStatus", colProps: { xs: 12, sm: 4 } },
      { field: "type", type: "multiple", select: "depreciationScheduleType", colProps: { xs: 12, sm: 4 } },
    ],
    [
      { field: "method", type: "multiple", select: "depreciationScheduleMethod", colProps: { xs: 12, md: 4 } },
      { field: "orgaRegistration", label: "Organization", searchEntityName: "Person", query: { type: "O" }, colProps: { xs: 12, md: 4 } },
      { field: "assetRegistration", label: "Asset", searchEntityName: "Asset", colProps: { xs: 12, md: 4 } },
    ],
    [
      { field: "fromStartDate", type: "date", mustBeLessThan: "toStartDate", colProps: { xs: 12, md: 3 } },
      { field: "toStartDate", type: "date", mustBeGreaterThan: "fromStartDate", colProps: { xs: 12, md: 3 } },
      { field: "fromEndDate", type: "date", mustBeLessThan: "toEndDate", colProps: { xs: 12, md: 3 } },
      { field: "toEndDate", type: "date", mustBeGreaterThan: "fromEndDate", colProps: { xs: 12, md: 3 } },
    ],
  ]

  const collateralSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 6, lg: 3 } },
      { field: "name", colProps: { xs: 12, md: 6, lg: 3 }, regex: true },
      { field: "status", colProps: { xs: 12, md: 6, lg: 3 }, select: "collateralStatus", type: "multiple" },
      { field: "type", colProps: { xs: 12, md: 6, lg: 3 }, select: "collateralType", type: "multiple" },
    ],
  ]

  const privateEquityAssetSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "status", type: "multiple", select: "privateEquityAssetStatus", colProps: { xs: 12, sm: 3 } },
      { field: "type", type: "multiple", select: "privateEquityAssetType", colProps: { xs: 12, sm: 3 } },
      { field: "code", type: "multiple", select: "privateEquityAssetCode", colProps: { xs: 12, sm: 3 } },
    ],
    [{ field: "name", colProps: { xs: 12, sm: 6 }, regex: true }],
  ]

  const privateEquityOperationSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "status", type: "multiple", select: "privateEquityOperationStatus", colProps: { xs: 12, sm: 3 } },
      { field: "type", type: "multiple", select: "privateEquityOperationType", colProps: { xs: 12, sm: 3 } },
      { field: "code", type: "multiple", select: "privateEquityOperationCode", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "transactionDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "paymentDate", type: "date", colProps: { xs: 12, sm: 3 } },
      { field: "amount", type: "currency", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const questionSearchFields = [
    [
      { field: "subject", colProps: { xs: 12 }, regex: true },
      { label: "Author", field: "_insertUser", colProps: { xs: 12, sm: 4 }, regex: true },
      { field: "status", type: "multiple", select: "questionStatus", colProps: { xs: 12, sm: 4 } },
      { field: "type", type: "multiple", select: "questionType", colProps: { xs: 12, sm: 4 } },
      { field: "keywords", type: "multiple", select: "questionKeyword", colProps: { xs: 12, sm: 8 } },
      { field: "locale", select: "locale", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const scoreSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 8 }, regex: true },
    ],
    [
      { field: "status", type: "multiple", label: loc`Status`, select: "scoreStatus", colProps: { xs: 12, sm: 4 } },
      { field: "type", type: "multiple", label: loc`Type`, select: "scoreType", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const bankAccountSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 4 }, regex: true },
    ],
    [{ field: "status", type: "multiple", select: "bankAccountStatus", colProps: { xs: 12, sm: 8 } }],
  ]

  const bankCardSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "bankAccountRegistration", label: "Bank account", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "type", type: "multiple", select: "bankCardType", colProps: { xs: 12, sm: 6 } },
      { field: "status", type: "multiple", select: "bankCardStatus", colProps: { xs: 12, sm: 6 } },
    ],
  ]

  const bankTransferOrderSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 3 } },
      { field: "name", colProps: { xs: 12, sm: 3 }, regex: true },
      { field: "sourceAccountRegistration", label: "Debit account", colProps: { xs: 12, sm: 3 } },
      { field: "beneficiaryBankIban", label: "Beneficiary IBAN", colProps: { xs: 12, sm: 3 } },
    ],
    [
      { field: "type", type: "multiple", select: "bankTransferOrderType", colProps: { xs: 12, sm: 3 } },
      { field: "status", type: "multiple", select: "bankTransferOrderStatus", colProps: { xs: 12, sm: 3 } },
      { field: "frequency", label: "Frequency", type: "multiple", select: "bankTransferOrderFrequency", colProps: { xs: 12, sm: 3 } },
    ],
  ]

  const featureFlagSearchFields = [
    [
      { field: "name", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "status", type: "multiple", select: "featureFlagStatus", colProps: { xs: 12, md: 3 } },
      { field: "type", type: "multiple", select: "featureFlagType", colProps: { xs: 12, md: 3 } },
    ],
  ]

  const flowSearchFields = [
    [
      { field: "accessKey", colProps: { xs: 12, sm: 4 } },
      { field: "pageId", colProps: { xs: 12, sm: 4 } },
      { field: "page", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const ledgerAccountSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 6 } },
      { field: "code", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "status", type: "multiple", select: "ledgerAccountStatus", colProps: { xs: 12, md: 6 } },
      { field: "type", type: "multiple", select: "ledgerAccountType", colProps: { xs: 12, md: 6 } },
      { field: "gaap", select: "accountingGaap", colProps: { xs: 12, md: 6 } },
      { field: "orgaRegistration", label: "Organization", searchEntityName: "Person", query: { type: "O" }, colProps: { xs: 12, md: 6 } },
      { field: "category", colProps: { xs: 12, md: 6 } },
      { field: "parentRegistration", label: "Parent", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "name", colProps: { xs: 12, md: 6 }, regex: true },
    ],
  ]

  const ledgerPeriodSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, md: 6 } },
      { field: "code", colProps: { xs: 12, md: 6 }, regex: true },
      { field: "status", type: "multiple", select: "ledgerPeriodStatus", colProps: { xs: 12, md: 6 } },
      { field: "type", type: "multiple", select: "ledgerPeriodType", colProps: { xs: 12, md: 6 } },
      { field: "orgaRegistration", label: "Organization", searchEntityName: "Person", query: { type: "O" }, colProps: { xs: 12, md: 6 } },
      { field: "fiscalYear", colProps: { xs: 12, md: 6 } },
    ],
  ]

  const eInvoiceLotSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "name", colProps: { xs: 12, sm: 4 }, regex: true },
      { field: "type", colProps: { xs: 12, sm: 4 }, select: "eInvoiceLotType" },
    ],
    [
      { field: "status", colProps: { xs: 12, sm: 4 }, select: "eInvoiceLotStatus" },
      { field: "triggerReason", colProps: { xs: 12, sm: 4 }, select: "eInvoiceLotTriggerReason" },
    ],
    [
      { field: "from_insertDate", label: "From build date", type: "date", mustBeLessThan: "to_insertDate", colProps: { xs: 12, sm: 4 } },
      { field: "to_insertDate", label: "To build date", type: "date", mustBeGreaterThan: "from_insertDate", colProps: { xs: 12, sm: 4 } },
    ],
    [
      {
        field: "from_transmissionDate",
        label: "From transmission date",
        type: "date",
        mustBeLessThan: "to_transmissionDate",
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "to_transmissionDate",
        label: "To transmission date",
        type: "date",
        mustBeGreaterThan: "from_transmissionDate",
        colProps: { xs: 12, sm: 4 },
      },
    ],
    [
      {
        field: "from_cfeReceivedDate",
        label: "From CFE received date",
        type: "date",
        mustBeLessThan: "to_cfeReceivedDate",
        colProps: { xs: 12, sm: 4 },
      },
      {
        field: "to_cfeReceivedDate",
        label: "To CFE received date",
        type: "date",
        mustBeGreaterThan: "from_cfeReceivedDate",
        colProps: { xs: 12, sm: 4 },
      },
    ],
  ]

  const paymentFileSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "externalDataRegistration", colProps: { xs: 12, sm: 4 } },
      { field: "type", colProps: { xs: 12, sm: 4 } },
      { field: "code", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const paymentFileLineSearchFields = [
    [
      { field: "registration", colProps: { xs: 12, sm: 4 } },
      { field: "paymentFileRegistration", colProps: { xs: 12, sm: 4 } },
      { field: "type", colProps: { xs: 12, sm: 4 } },
      { field: "code", colProps: { xs: 12, sm: 4 } },
    ],
  ]

  const lowerEntityName = entityName.toLowerCase()
  if (lowerEntityName === "agreement") return agreementSearchFields
  if (lowerEntityName === "aiconversation") return aiConversationSearchFields
  if (lowerEntityName === "aimemory") return aiMemorySearchFields
  if (lowerEntityName === "asset") return assetSearchFields
  if (lowerEntityName === "assetcatalogitem") return assetCatalogItemSearchFields
  if (lowerEntityName === "assetlot") return assetLotSearchFields
  if (lowerEntityName === "amendment") return amendmentSearchFields
  if (lowerEntityName === "bankaccount") return bankAccountSearchFields
  if (lowerEntityName === "bankcard") return bankCardSearchFields
  if (lowerEntityName === "banktransferorder") return bankTransferOrderSearchFields
  if (lowerEntityName === "case") return caseSearchFields
  if (lowerEntityName === "claim") return claimSearchFields
  if (lowerEntityName === "collateral") return collateralSearchFields
  if (lowerEntityName === "campaign") return campaignSearchFields
  if (lowerEntityName === "committee") return committeeSearchFields
  if (lowerEntityName === "communication") return communicationSearchFields
  if (lowerEntityName === "contract") return contractSearchFields
  if (lowerEntityName === "contractlot") return contractLotSearchFields
  if (lowerEntityName === "covenant") return covenantSearchFields
  if (lowerEntityName === "counter") return counterSearchFields
  if (lowerEntityName === "creditline") return creditLineSearchFields
  if (lowerEntityName === "deal") return dealSearchFields
  if (lowerEntityName === "document") return documentSearchFields
  if (lowerEntityName === "einvoicelot") return eInvoiceLotSearchFields
  if (lowerEntityName === "featureflag") return featureFlagSearchFields
  if (lowerEntityName === "fine") return fineSearchFields
  if (lowerEntityName === "flow") return flowSearchFields
  if (lowerEntityName === "invoice") return invoiceSearchFields
  if (lowerEntityName === "ledgeraccount") return ledgerAccountSearchFields
  if (lowerEntityName === "ledgerperiod") return ledgerPeriodSearchFields
  if (lowerEntityName === "maintenance") return maintenanceSearchFields
  if (lowerEntityName === "paymentfile") return paymentFileSearchFields
  if (lowerEntityName === "paymentfileline") return paymentFileLineSearchFields
  if (lowerEntityName === "person") return personSearchFields
  if (lowerEntityName === "privateequityasset") return privateEquityAssetSearchFields
  if (lowerEntityName === "privateequityoperation") return privateEquityOperationSearchFields
  if (lowerEntityName === "posting") return postingSearchFields
  if (lowerEntityName === "postingevent") return postingEventSearchFields
  if (lowerEntityName === "product") return productSearchFields
  if (lowerEntityName === "project") return projectSearchFields
  if (lowerEntityName === "prospect") return personSearchFields
  if (lowerEntityName === "question") return questionSearchFields
  if (lowerEntityName === "score") return scoreSearchFields
  if (lowerEntityName === "scriptlog") return scriptLogSearchFields
  if (lowerEntityName === "sepa") return sepaSearchFields
  if (lowerEntityName === "task") return taskSearchFields
  if (lowerEntityName === "user") return getUserSearchFields()
  if (lowerEntityName === "depreciationschedule") return depreciationScheduleSearchFields

  // Default fields
  return [
    [
      { field: "registration", colProps: { xs: 12, md: 3 } },
      { field: "name", colProps: { xs: 12, md: 9 }, regex: true },
    ],
  ]
}

export function getEntitySearchFieldNames(entityName) {
  const { searchFields } = getEntitySearchFields(entityName)
  return toFieldNames(searchFields)
}

export function getEntityDefaultConfig(entityName) {
  if (!entityName) return

  const contractElementColumns = [
    { title: "Registration", name: "registration", linkTo: "/contract-element/{registration}" },
    { title: "Type", name: "type", select: "elementType" },
    { title: "Code", name: "code" },
    { title: "Name", name: "name" },
    { title: "Start date", name: "startDate" },
    { title: "End date", name: "endDate" },
    { title: "Contract registration", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    { title: "Payee registration", name: "payeeRegistration", linkTo: "/person/{payeeRegistration}" },
    { title: "Payer registration", name: "payerRegistration", linkTo: "/person/{payerRegistration}" },
    { title: "Total payment", name: "totalPayment", type: "currency" },
    { title: "Currency", name: "currency" },
    { title: "GroupKey", name: "groupKey" },
    { title: "Quotation registration", name: "quotationRegistration" },
    { title: "PR", name: "pr" },
    { title: "Status", name: "status", select: "contractElementStatus", badge: true },
  ]

  const contractLotColumns = [
    { title: "Registration", name: "registration", linkTo: "/contract-lot/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    // { title: "Code", name: "code" },
    { title: "Type", name: "type", select: "contractLotType" },
    { title: "Name", name: "name" },
    { title: "Fund", name: "fundRegistration", linkTo: "/fund/{fundRegistration}" },
    { title: "Rate", name: "rate", type: "percentage" },
    { title: "Cession date", name: "cessionDate", type: "date" },
    { title: "Total amount", name: "totalAmount", type: "currency" },
    { title: "Submit date", name: "submitDate", type: "date" },
    { title: "Accept date", name: "acceptDate", type: "date" },
    { title: "Validation date", name: "validationDate", type: "date" },
    { title: "Status", name: "status", select: "contractLotStatus", badge: true },
    // { title: "Internal reference", name: "internalReference" },
    // { title: "External reference", name: "externalReference" },
  ]

  const counterColumns = [
    { title: "Name", name: "name" },
    { title: "Seq", name: "seq" },
  ]

  const covenantColumns = [
    { title: "Registration", name: "registration", linkTo: "/covenant/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Code", name: "code" },
    { title: "Type", name: "type", select: "covenantType" },
    { title: "Status", name: "status", select: "covenantStatus", badge: true },
  ]

  const postingColumns = [
    { title: "Registration", name: "registration", linkTo: "/posting/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Event type", name: "eventType" },
    { title: "Status", name: "status", select: "postingStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Date", name: "date", type: "datetime" },
    { title: "D/C", name: "dc", select: "postingDc" },
    { title: "Account", name: "account", select: "ledgerAccount" },
    { title: "Account details", name: "secondaryAccount", select: "ledgerAccount" },
    { title: "Amount", name: "amount", excelFormat: "currency", type: "currency" },
    { title: "Document Ref", name: "documentReference" },
    { title: "Ref", name: "auditTrailReference" },
    { title: "Type", name: "type", select: "postingType" },
    { title: "Code", name: "code", select: "postingCode" },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/{assetRegistration}" },
    { title: "Person", name: "personRegistration", linkTo: "/person/{personRegistration}" },
    { title: "Invoice", name: "invoiceRegistration", linkTo: "/invoice/{invoiceRegistration}" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    { title: "Cashflow", name: "cashflowRegistration", linkTo: "/cashflow/{cashflowRegistration}" },
    { title: "Creditline", name: "creditlineRegistration", linkTo: "/credit-line/{creditlineRegistration}" },
  ]

  const aiConversationColumns = [
    { title: "Registration", name: "registration", linkTo: "/ai-conversation/{registration}", className: "w-7" },
    { title: "Parent", name: "parentConversationRegistration", linkTo: "/ai-conversation/{parentConversationRegistration}", className: "w-7" },
    { title: "Agent", name: "aiAgentRegistration", linkTo: "/ai-agent/{aiAgentRegistration}", className: "w-7" },
    { title: "Provider", name: "provider", className: "w-10" },
    { title: "Model", name: "model", className: "w-12" },
    { title: "Status", name: "status", select: "aiConversationStatus", className: "w-5", badge: true },
    { title: "Type", name: "type", select: "aiConversationType", className: "w-7" },
    { title: "Code", name: "code", select: "aiConversationCode", className: "w-7" },
    { title: "Mode", name: "mode", select: "aiConversationMode", className: "w-7" },
    { title: "Subject", name: "subject" },
    { title: "Update user", name: "_updateUser", className: "w-10" },
    { title: "Update date", name: "_updateDate", type: "datetime", formInputProps: { timeFormat: "HH:mm:ss" } },
  ]

  const aiAgentColumns = [
    { title: "Registration", name: "registration", linkTo: "/ai-agent/{registration}", className: "w-7" },
    { title: "Name", name: "name" },
    { title: "Description", name: "description" },
    { title: "Status", name: "status", select: "aiAgentStatus", className: "w-5", badge: true },
    { title: "Update user", name: "_updateUser", className: "w-10" },
    { title: "Update date", name: "_updateDate", type: "datetime" },
  ]

  const aiMemoryColumns = [
    { title: "Registration", name: "registration", linkTo: "/ai-memory/{registration}", className: "w-7" },
    { title: "Type", name: "type", select: "aiMemoryType", className: "w-7" },
    { title: "Person", name: "personRegistration", linkTo: "/person/{personRegistration}", className: "w-7" },
    { title: "User", name: "username", className: "w-7" },
    { title: "Agent", name: "aiAgentRegistration", linkTo: "/ai-agent/{aiAgentRegistration}", className: "w-7" },
    { title: "Memory", name: "text" },
    { title: "Status", name: "status", select: "aiMemoryStatus", className: "w-5", badge: true },
    { title: "Conversation", name: "aiConversationRegistration", linkTo: "/ai-conversation/{aiConversationRegistration}", className: "w-7" },
    { title: "Update user", name: "_updateUser", className: "w-10" },
    { title: "Update date", name: "_updateDate", type: "datetime" },
  ]

  const bankCardColumns = [
    { title: "Registration", name: "registration", linkTo: "/bank-card/{registration}" },
    { title: "Bank account", name: "bankAccountRegistration", linkTo: "/bank-account/{registration}" },
    { title: "Status", name: "status", select: "bankCardStatus", badge: true },
    { title: "Type", name: "type", select: "bankCardType" },
    { title: "Name", name: "name" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "Expiration date", name: "expirationDate", type: "date" },
  ]

  const bankTransferOrderFields = [
    { name: "client", path: "persons[role=CLIENT].personRegistration" },
    { name: "beneficiary", path: "persons[role=BENEFICIARY].personRegistration" },
  ]

  const bankTransferOrderColumns = [
    { title: "Registration", name: "registration", linkTo: "/bank-transfer-order/{registration}" },
    { title: "Status", name: "status", select: "bankTransferOrderStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Client", name: "client", linkTo: "/person/{client}" },
    { title: "Debit account", name: "sourceAccountRegistration", linkTo: "/bank-account/{sourceAccountRegistration}" },
    { title: "Type", name: "type", select: "bankTransferOrderType" },
    { title: "Frequency", name: "schedule.frequency", select: "bankTransferOrderFrequency" },
    { title: "Next execution", name: "nextExecutionDate", type: "date" },
    { title: "Currency", name: "currency" },
    { title: "Amount", name: "amount", excelFormat: "currency", type: "currency" },
  ]

  const campaignColumns = [
    { title: "Registration", name: "registration", linkTo: "/campaign/{registration}" },
    { title: "Name", name: "name" },
    { title: "Status", name: "status", select: "campaignStatus", badge: true },
    { title: "Type", name: "type", select: "campaignType" },
  ]

  const postingEventColumns = [
    { title: "Registration", name: "registration", linkTo: "/posting-event/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Event type", name: "eventType" },
    { title: "Status", name: "status", select: "postingEventStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Date", name: "date", type: "datetime" },
    { title: "Type", name: "type", select: "postingEventType" },
    { title: "Code", name: "code", select: "postingEventCode" },
    { title: "Account", name: "account", select: "ledgerAccount" },
    { title: "Account details", name: "secondaryAccount", select: "ledgerAccount" },
    { title: "Amount", name: "amount", excelFormat: "currency" },
    { title: "Document Ref", name: "documentReference" },
    { title: "Ref", name: "auditTrailReference" },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/{assetRegistration}" },
    { title: "Person", name: "personRegistration", linkTo: "/person/{personRegistration}" },
    { title: "Invoice", name: "invoiceRegistration", linkTo: "/invoice/{invoiceRegistration}" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    { title: "Cashflow", name: "cashflowRegistration", linkTo: "/cashflow/{cashflowRegistration}" },
    { title: "Creditline", name: "creditlineRegistration", linkTo: "/credit-line/{creditlineRegistration}" },
  ]

  const paymentFileColumns = [
    { title: "Registration", name: "registration", linkTo: "/payment-file/{registration}", className: "w-10" },
    { title: "External data", name: "externalDataRegistration", linkTo: "/external-data/{externalDataRegistration}", className: "w-10" },
    { title: "Organization", name: "organization.name", hidden: true, className: "w-10" },
    { title: "Status", name: "status", select: "paymentFileStatus", badge: true, className: "w-10" },
    { title: "File date", name: "fileDate", type: "datetime", className: "w-10" },
    { title: "Type", name: "type", select: "paymentFileType" },
  ]

  const paymentFileLineColumns = [
    { title: "Registration", name: "registration", linkTo: "/payment-file-line/{registration}", className: "w-10" },
    { title: "Status", name: "status", select: "paymentFileLineStatus", badge: true, className: "w-10" },
    { title: "Type", name: "type", select: "paymentFileLineType" },
    { title: "Code", name: "code" },
    { title: "Amount", name: "amount", type: "number" },
    { title: "Currency", name: "currency", select: "currency" },
    { title: "Reference", name: "reference" },
    { title: "Value date", name: "valueDate", type: "date" },
    { title: "Booking date", name: "bookingDate", type: "date" },
    { title: "Settlement date", name: "settlementDate", type: "date" },
    { title: "Transaction date", name: "transactionDate", type: "date" },
    { title: "Transaction ID", name: "transactionId" },
    { title: "Line number", name: "lineNumber" },
    { title: "Payment file", name: "paymentFileRegistration", linkTo: "/payment-file/{paymentFileRegistration}" },
  ]

  const assetCatalogItemColumns = [
    { title: "Registration", name: "registration", linkTo: "/asset-catalog-item/{registration}" },
    { title: "Type", name: "type", select: "assetCatalogItemType" },
    { title: "Code", name: "code", select: "assetCatalogItemCode" },
    { title: "Name", name: "name" },
    { title: "Status", name: "status", select: "assetCatalogItemStatus", badge: true },
    { title: "Catalog", name: "catalogName" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Internal reference", name: "internalReference" },
    { title: "External reference", name: "externalReference" },
  ]

  const projectColumns = [
    { title: "Registration", name: "registration", linkTo: "/project/{registration}" },
    { title: "Status", name: "status", select: "projectStatus", badge: true },
    { title: "Organization", name: "organization.name", hidden: true, sortableOnServer: false },
    { title: "Owner", name: "owner.name", linkTo: "/person/{ownerRegistration}", sortableOnServer: false },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "projectType", hidden: true },
    { title: "Client", name: "client", hidden: true, sortableOnServer: false },
    { title: "Organization", name: "orgaRegistration", hidden: true },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Insert date", name: "_insertDate", type: "date", hidden: true },
    { title: "Update date", name: "_updateDate", type: "date", hidden: true },
    { title: "Update user", name: "_updateUser", hidden: true },
  ]

  const caseColumns = [
    { title: "Registration", name: "registration", linkTo: "/case/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Date", name: "date", type: "date" },
    { title: "Status", name: "status", select: "caseStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "caseType" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    { title: "Invoice", name: "invoiceRegistration", linkTo: "/invoice/{invoiceRegistration}" },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/{assetRegistration}" },
    { title: "Asset lot", name: "assetLotRegistration", linkTo: "/asset-lot/{assetLotRegistration}" },
    { title: "Amount", name: "amount", type: "currency" },
    { title: "Score", name: "score" },
    { title: "Last contact", name: "lastContact" },
    { title: "Last action", name: "lastAction" },
  ]

  const claimColumns = [
    { title: "Registration", name: "registration", linkTo: `/claim/{registration}` },
    { title: "Status", name: "status", select: "claimStatus", badge: true },
    { title: "Code", name: "code" },
    { title: "Name", name: "name" },
    { title: "Description", name: "description" },
    { title: "Internal Reference", name: "internalReference" },
    { title: "External Reference", name: "externalReference" },
    { title: "Start Date", name: "startDate", type: "date" },
    { title: "End Date", name: "endDate", type: "date" },
  ]

  const maintenanceFields = [{ name: "supplier", path: "persons[role=SUPPLIER].personRegistration" }]

  const lessonCatalogItemColumns = [
    { title: "Registration", name: "registration", linkTo: "/lesson-catalog-item/{registration}" },
    { title: "Status", name: "status", select: "lessonCatalogItemStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Code", name: "code" },
    { title: "Version", name: "version" },
  ]

  const trainingCatalogItemColumns = [
    { title: "Registration", name: "registration", linkTo: "/training-catalog-item/{registration}" },
    { title: "Status", name: "status", select: "trainingCatalogItemStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Code", name: "code" },
    { title: "Version", name: "version" },
    { title: "Level", name: "level", select: "trainingLevel" },
    { title: "Categories", name: "categories", select: "trainingCategory", multiple: true },
    { title: "Requires the course", name: "requiresCourseCode" },
  ]

  const trainingFields = [{ name: "trainee", path: "persons[role=TRAINEE].personRegistration" }]

  const trainingColumns = [
    { title: "Registration", name: "registration", linkTo: "/training/{registration}" },
    { title: "Status", name: "status", select: "trainingStatus", badge: true },
    { title: "Trainee", name: "trainee", linkTo: "/person/{trainee}" },
    { title: "Course", name: "trainingCatalogItemRegistration", linkTo: "/training-catalog-item/{trainingCatalogItemRegistration}" },
    { title: "Name", name: "name" },
    { title: "Version", name: "trainingCatalogItemVersion" },
    { title: "Lessons completed", name: "completedLessons" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
  ]

  const evaluationCatalogItemColumns = [
    { title: "Registration", name: "registration", linkTo: "/evaluation-catalog-item/{registration}" },
    { title: "Status", name: "status", select: "evaluationCatalogItemStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Code", name: "code" },
    { title: "Version", name: "version" },
    { title: "Pass score", name: "passScore" },
  ]

  const lessonFields = [{ name: "trainee", path: "persons[role=TRAINEE].personRegistration" }]

  const lessonColumns = [
    { title: "Registration", name: "registration", linkTo: "/lesson/{registration}" },
    { title: "Status", name: "status", select: "lessonStatus", badge: true },
    { title: "Trainee", name: "trainee", linkTo: "/person/{trainee}" },
    { title: "Name", name: "name" },
    { title: "Version", name: "lessonCatalogItemVersion" },
    { title: "Completed steps", name: "completedSteps" },
    { title: "Total steps", name: "totalSteps" },
    { title: "Score", name: "score" },
    { title: "Start date", name: "startDate", type: "date" },
  ]

  const evaluationFields = [{ name: "trainee", path: "persons[role=TRAINEE].personRegistration" }]

  const evaluationColumns = [
    { title: "Registration", name: "registration", linkTo: "/evaluation/{registration}" },
    { title: "Status", name: "status", select: "evaluationStatus", badge: true },
    { title: "Trainee", name: "trainee", linkTo: "/person/{trainee}" },
    { title: "Lesson", name: "lessonRegistration", linkTo: "/lesson/{lessonRegistration}" },
    { title: "Step code", name: "stepCode" },
    { title: "Attempt", name: "attempt" },
    { title: "Score", name: "score" },
    { title: "Submitted", name: "submittedDate", type: "datetime" },
  ]

  const fineColumns = [
    { title: "Registration", name: "registration", linkTo: `/fine/{registration}` },
    { title: "Status", name: "status", select: "fineStatus", badge: true },
    { title: "Type", name: "type", select: "fineType" },
    { title: "Code", name: "code" },
    { title: "Name", name: "name" },
    { title: "Description", name: "description" },
    { title: "Amount", name: "amount", type: "currency" },
    { title: "Issue date", name: "issueDate", type: "date" },
    { title: "Reception date", name: "receptionDate", type: "date" },
  ]

  const scriptLogColumns = [
    { title: "Request ID", name: "requestId", className: "w-5" },
    { title: "Script name", name: "scriptName", className: "w-10" },
    { title: "Username", name: "username", className: "w-10" },
    { title: "Line", name: "lineNumber", className: "w-5" },
    { title: "Column", name: "columnNumber", className: "w-5", hidden: true, available: true },
    { title: "Level", name: "level", className: "w-5", select: "scriptLogLevel", hidden: true, available: true },
    { title: "Insert date", name: "_insertDate", type: "datetime", formInputProps: { timeFormat: "HH:mm:ss.SSS" }, className: "w-10" },
    { title: "Expiration date", name: "expirationDate", type: "datetime", formInputProps: { timeFormat: "HH:mm:ss.SSS" }, className: "w-10" },
    { title: "Output", name: "output", type: "object", className: "w-50" },
  ]

  const communicationColumns = [
    { title: "Registration", name: "registration", linkTo: "/communication/{registration}" },
    { title: "Date", name: "date", type: "datetime" },
    { title: "Status", name: "status", select: "communicationStatus", badge: true },
    { title: "Subject", name: "subject" },
    { title: "Type", name: "type", select: "communicationType" },
    { title: "Category", name: "category", select: "communicationCategory" },
    { title: "Person", name: "persons.0.person.name", linkTo: "/person/{persons.0.personRegistration}" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    { title: "Invoice", name: "invoiceRegistration", linkTo: "/invoice/{invoiceRegistration}" },
  ]

  const maintenanceColumns = [
    { title: "Registration", name: "registration", linkTo: "/maintenance/{registration}" },
    { title: "Organization", name: "organization", linkTo: "/person/{orgaRegistration}" },
    { title: "Asset", name: "items.0.assetRegistration", linkTo: "/asset/{items.0.assetRegistration}", hidden: true },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/{assetRegistration}" },
    { title: "Status", name: "status", select: "maintenanceStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "maintenanceType" },
    { title: "Supplier", name: "supplier", linkTo: "/person/{supplier}" },
    { title: "Submit date", name: "submitDate", type: "date" },
    { title: "Return date", name: "returnDate", type: "date" },
    { title: "Validation date", name: "validationDate", type: "date" },
    { title: "Amount (excl. tax)", name: "amountExclTax", type: "currency" },
    { title: "Amount (incl. tax)", name: "amountInclTax", type: "currency" },
  ]

  const collateralColumns = [
    { title: "Registration", name: "registration", linkTo: "/collateral/{registration}" },
    { title: "Status", name: "status", badge: true, select: "collateralStatus" },
    { title: "Name", name: "name" },
    { title: "Description", name: "description" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Amount", name: "amount", type: "currency" },
    { title: "Percentage", name: "percentage" },
    { title: "Person registration", name: "personRegistration" },
    { title: "Type", name: "type", select: "collateralType" },
  ]

  const dealOrContractColumns = [
    { title: "Name", name: "name" },
    { title: "Product", name: "quotations[0].financialProduct", select: "financialProduct" },
    { title: "Start date", name: "quotations[0].startDate", type: "date" },
    { title: "Amount", name: "quotations[0].financedAmountExclTax", type: "currency", currencyPath: "persons[0].invoicing.principalCurrency" },
  ]

  const contractColumns = [
    { title: "Registration", name: "registration", linkTo: "/contract/{registration}" },
    { title: "Status", name: "status", badge: true, select: "contractStatus" },
    ...dealOrContractColumns,
  ]

  const dealColumns = [
    { title: "Registration", name: "registration", linkTo: "/deal/{registration}" },
    { title: "Status", name: "status", badge: true, select: "dealStatus" },
    ...dealOrContractColumns,
  ]

  const documentColumns = [
    { title: "Registration", name: "registration", linkTo: "/document/{registration}" },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: `/api/script/runs/${frontScripts.documentTypesWithWorkflow}?entityNames={entityName}` },
    { title: "Status", name: "status", badge: true, select: "documentStatus" },
    { title: "URL", name: "url", linkTo: "{url}" },
    { title: "Size", name: "size", type: "bytes" },
    { title: "Entity name", name: "entityName", select: `/api/script/runs/${frontScripts.documentEntitiesWithWorkflow}?entityName={entityName}` },
    { title: "Entity registration", name: "entityRegistration", linkTo: "{entityName}/{entityRegistration}", getEntityNameClientRoute: true },
  ]

  const agreementColumns = [
    { title: "Registration", name: "registration", linkTo: "/agreement/{registration}" },
    { title: "Status", name: "status", badge: true, select: "agreementStatus" },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "agreementType" },
  ]

  const invoiceColumns = [
    { title: "Registration", name: "registration", linkTo: "/invoice/{registration}" },
    { title: "Status", name: "status", badge: true, select: "invoiceStatus" },
    { title: "Name", name: "name" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/{contractRegistration}" },
    {
      title: "Payer",
      name: "payerRegistration",
      field: "name",
      findIn: "persons",
      findKey: "role",
      findValue: personRoles.PAYER,
      foundKey: "personRegistration",
      linkTo: "/person/{personRegistration}",
    },
    {
      title: "Payee",
      name: "payeeRegistration",
      field: "name",
      findIn: "persons",
      findKey: "role",
      findValue: personRoles.PAYEE,
      foundKey: "personRegistration",
      linkTo: "/person/{personRegistration}",
    },
    { title: "Amount", name: "amountExclTax", type: "currency", currencyPath: "currency" },
  ]

  const ledgerAccountColumns = [
    { title: "Registration", name: "registration", linkTo: "/ledger-account/{registration}" },
    { title: "Code", name: "code" },
    { title: "Status", name: "status", badge: true, select: "ledgerAccountStatus" },
    { title: "Type", name: "type", select: "ledgerAccountType" },
    { title: "Gaap", name: "gaap" },
    { title: "Organization", name: "orgaRegistration" },
    { title: "Name", name: "name" },
    { title: "Parent", name: "parentRegistration" },
    { title: "Reconcilable", name: "reconcilable", type: "checkbox", hidden: true, available: true },
    { title: "Normal side", name: "normalSide", hidden: true, available: true },
    { title: "Category", name: "category", select: "ledgerAccountCategory" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Description", name: "description", hidden: true, available: true },
  ]

  const ledgerPeriodColumns = [
    { title: "Registration", name: "registration", linkTo: "/ledger-period/{registration}" },
    { title: "Code", name: "code" },
    { title: "Status", name: "status", badge: true, select: "ledgerPeriodStatus" },
    { title: "Type", name: "type", select: "ledgerPeriodType" },
    { title: "Organization", name: "orgaRegistration" },
    { title: "Name", name: "name" },
    { title: "Fiscal year", name: "fiscalYear" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
  ]

  const personColumns = [
    { title: "Registration", name: "registration", linkTo: "/person/{registration}" },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "personType" },
    { title: "Address", name: "addresses[0].addressLine" },
  ]

  const prospectColumns = [
    { title: "Registration", name: "registration", linkTo: "/prospect/{registration}" },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "personType" },
    { title: "Address", name: "addresses[0].addressLine" },
  ]

  const creditLineColumns = [
    { title: "Registration", name: "registration", linkTo: "/credit-line/{registration}" },
    { title: "Status", name: "status", badge: true, select: "creditLineStatus" },
    { title: "Name", name: "name" },
    { title: "Limit", name: "creditLimit", type: "currency", currencyPath: "currency" },
    { title: "Outstanding", name: "outstanding", type: "currency", currencyPath: "currency" },
    { title: "Available", name: "available", type: "currency", currencyPath: "currency" },
  ]

  const assetColumns = [
    { title: "Registration", name: "registration", linkTo: "/asset/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Status", name: "status", badge: true, select: "assetStatus" },
    { title: "Name", name: "name" },
    { title: "Identification number", name: "identificationNumber", hidden: true },
    { title: "Registration book number", name: "registrationBookNumber", hidden: true },
    { title: "Price", name: "priceExclTax", type: "currency", currencyPath: "currency", excelFormat: "currency" },
    { title: "Incl. tax", name: "priceInclTax", type: "currency", currencyPath: "currency", excelFormat: "currency" },
    { title: "Type", name: "type", select: "assetType" },
  ]

  const productColumns = [
    { title: "Registration", name: "registration", linkTo: "/product/{registration}" },
    { title: "Name", name: "name" },
    { title: "Code", name: "code" },
    { title: "Status", name: "status", badge: true, select: "productStatus" },
  ]

  const committeeColumns = [
    { title: "Registration", name: "registration", linkTo: "/committee/{registration}" },
    { title: "Name", name: "name" },
    { title: "Code", name: "code" },
    { title: "Status", name: "status", badge: true, select: "committeeStatus" },
  ]

  const fundColumns = [
    { title: "Registration", name: "registration", linkTo: "/fund/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Status", name: "status", select: "fundStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "fundType" },
    { title: "Financing type", name: "financingType", select: "fundFinancingType" },
    { title: "Financing nature", name: "financingNature", select: "fundFinancingNature" },
    { title: "Financing finality", name: "financingFinality", select: "fundFinancingFinality" },
    { title: "Management type", name: "managementType", select: "managementType" },
    { title: "Credit limit", name: "creditLimit", type: "currency", excelFormat: "currency" },
    { title: "Outstanding", name: "outstanding", type: "currency", excelFormat: "currency" },
    { title: "Available", name: "available", type: "currency", excelFormat: "currency" },
  ]

  const assetLotColumns = [
    { title: "Registration", name: "registration", linkTo: "/asset-lot/{registration}" },
    { title: "Name", name: "name" },
    { title: "Status", name: "status", badge: true, select: "assetLotStatus" },
  ]

  const privateEquityAssetColumns = [
    { title: "Registration", name: "registration", linkTo: "/private-equity-asset/{registration}" },
    { title: "Name", name: "name" },
    { title: "Status", name: "status", select: "privateEquityAssetStatus", badge: true },
    { title: "Type", name: "type", select: "privateEquityAssetType" },
    { title: "Code", name: "Code", select: "privateEquityAssetCode" },
  ]

  const privateEquityOperationColumns = [
    { title: "Registration", name: "registration", linkTo: "/private-equity-operation/{registration}" },
    { title: "Status", name: "status", select: "privateEquityOperationStatus", badge: true },
    { title: "Type", name: "type", select: "privateEquityOperationType" },
    { title: "Code", name: "code", select: "privateEquityOperationCode" },
    { title: "Label", name: "label" },
    { title: "Amount", name: "amount", type: "currency" },
    { title: "Commitment", name: "commitment", type: "currency" },
    { title: "Transaction date", name: "transactionDate", type: "date" },
    { title: "Payment date", name: "paymentDate", type: "date" },
    { title: "Nb. shares", name: "nbShares" },
  ]

  const questionColumns = [
    { title: "Registration", name: "registration", linkTo: "/question/{registration}" },
    { title: "Subject", name: "subject" },
    { title: "Status", name: "status", select: "questionStatus", badge: true },
    { title: "Type", name: "type", select: "questionType" },
    { title: "Locale", name: "locale" },
    { title: "Author", name: "_insertUser" },
    { title: "Keywords", name: "keywords", select: "questionKeyword", formInputProps: { multiple: true, displayMode: "raw" } },
  ]

  const scoreColumns = [
    { title: "Registration", name: "registration", linkTo: "/score/{registration}" },
    { title: "Organization", name: "organization.name", hidden: true },
    { title: "Status", name: "status", select: "scoreStatus", badge: true },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "scoreType" },
  ]

  const featureFlagColumns = [
    { title: "Name", name: "name", className: "w-20", linkTo: "/feature-flag/{name}" },
    { title: "Status", name: "status", select: "featureFlagStatus", className: "w-20", badge: true },
    { title: "Type", name: "type", select: "featureFlagType" },
  ]

  const userColumns = [
    { title: "Username", name: "username" },
    { title: "Name", name: "name" },
    { title: "Email", name: "email" },
    { title: "Profiles", name: "profiles" },
    { title: "Locale", name: "locale", select: "locale" },
    { title: "Person", name: "person", linkTo: "/person/{personRegistration}" },
    { title: "Partner", name: "partner", linkTo: "/person/{partnerRegistration}" },
    { title: "Organization", name: "organization", linkTo: "/person/{orgaRegistration}" },
  ]

  const inspectionColumns = [
    { title: "Registration", name: "registration", linkTo: "/inspection/{registration}" },
    { title: "Status", name: "status", select: "inspectionStatus", badge: true },
    { title: "Type", name: "type", select: "inspectionType" },
    { title: "Name", name: "name" },
    { title: "Date", name: "date", type: "datetime" },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/${assetRegistration}" },
    { title: "Contract", name: "contractRegistration", linkTo: "/contract/${contractRegistration}" },
  ]

  const assetServiceCatalogItemColumns = [
    { title: "Registration", name: "registration", linkTo: "/asset-service-catalog-item/{registration}" },
    { title: "Status", name: "status", select: "assetServiceCatalogItemStatus", badge: true },
    { title: "Code", name: "code" },
    { title: "Name", name: "name" },
    { title: "Description", name: "description" },
  ]

  const depreciationScheduleColumns = [
    { title: "Registration", name: "registration", linkTo: "/depreciation-schedule/{registration}" },
    { title: "Status", name: "status", select: "depreciationScheduleStatus", badge: true },
    { title: "Type", name: "type", select: "depreciationScheduleType" },
    { title: "Method", name: "method", select: "depreciationScheduleMethod" },
    { title: "Start date", name: "startDate", type: "date" },
    { title: "End date", name: "endDate", type: "date" },
    { title: "Asset", name: "assetRegistration", linkTo: "/asset/{assetRegistration}" },
  ]

  const camtEntryColumns = [
    { title: "Camt Entry", name: "registration", linkTo: "/camt/{camtRegistration}/entry/{registration}" },
    { title: "Cashflow", name: "cashflowRegistration", linkTo: "/cashflow/{cashflowRegistration}" },
    { title: "Invoice", name: "invoice", linkTo: "/invoice/{invoiceRegistration}" },
    { title: "Status", name: "cashflow.status", select: "cashflowStatus", badge: true },
    { title: "Date", name: "bookingDate", type: "date" },
    { title: "Payable/Receivable", name: "pr" },
    { title: "Payment codes", name: "paymentCodes" },
    { title: "End to end ID", name: "endToEndId" },
    { title: "Payer IBAN", name: "payerIban" },
    { title: "Creditor reference", name: "creditorReferenceInformation" },
    { title: "Payee IBAN", name: "payeeIban" },
    { title: "Amount", name: "amount", type: "currency" },
  ]

  const eInvoiceLotColumns = [
    { title: "Registration", name: "registration", linkTo: "/e-invoice-lot/{registration}" },
    { title: "Name", name: "name" },
    { title: "Type", name: "type", select: "eInvoiceLotType" },
    { title: "Status", name: "status", select: "eInvoiceLotStatus", badge: true },
    { title: "Trigger reason", name: "triggerReason", select: "eInvoiceLotTriggerReason" },
    { title: "Documents count", name: "bundledDocumentsCount" },
    { title: "Build date", name: "_insertDate", type: "datetime" },
    { title: "Transmission date", name: "transmissionDate", type: "datetime" },
    { title: "CFE received date", name: "cfeReceivedDate", type: "datetime" },
  ]

  const lowerEntityName = entityName.toLowerCase()
  if (lowerEntityName === "agreement") return { columns: agreementColumns }
  if (lowerEntityName === "aiagent") return { columns: aiAgentColumns }
  if (lowerEntityName === "aiconversation") return { columns: aiConversationColumns }
  if (lowerEntityName === "aimemory") return { columns: aiMemoryColumns }
  if (lowerEntityName === "asset") return { columns: assetColumns }
  if (lowerEntityName === "assetcatalogitem") return { columns: assetCatalogItemColumns }
  if (lowerEntityName === "assetlot") return { columns: assetLotColumns }
  if (lowerEntityName === "assetservicecatalogitem") return { columns: assetServiceCatalogItemColumns }
  if (lowerEntityName === "bankcard") return { columns: bankCardColumns }
  if (lowerEntityName === "banktransferorder") return { columns: bankTransferOrderColumns, fields: bankTransferOrderFields }
  if (lowerEntityName === "campaign") return { columns: campaignColumns }
  if (lowerEntityName === "camtentry") return { columns: camtEntryColumns }
  if (lowerEntityName === "case") return { columns: caseColumns }
  if (lowerEntityName === "claim") return { columns: claimColumns }
  if (lowerEntityName === "collateral") return { columns: collateralColumns }
  if (lowerEntityName === "committee") return { columns: committeeColumns }
  if (lowerEntityName === "communication") return { columns: communicationColumns }
  if (lowerEntityName === "contract") return { columns: contractColumns }
  if (lowerEntityName === "contractelement") return { columns: contractElementColumns }
  if (lowerEntityName === "contractlot") return { columns: contractLotColumns }
  if (lowerEntityName === "counter") return { columns: counterColumns }
  if (lowerEntityName === "covenant") return { columns: covenantColumns }
  if (lowerEntityName === "creditline") return { columns: creditLineColumns }
  if (lowerEntityName === "deal") return { columns: dealColumns }
  if (lowerEntityName === "depreciationschedule") return { columns: depreciationScheduleColumns }
  if (lowerEntityName === "document") return { columns: documentColumns }
  if (lowerEntityName === "einvoicelot") return { columns: eInvoiceLotColumns }
  if (lowerEntityName === "evaluation") return { columns: evaluationColumns, fields: evaluationFields }
  if (lowerEntityName === "evaluationcatalogitem") return { columns: evaluationCatalogItemColumns }
  if (lowerEntityName === "featureflag") return { columns: featureFlagColumns }
  if (lowerEntityName === "fine") return { columns: fineColumns }
  if (lowerEntityName === "fund") return { columns: fundColumns }
  if (lowerEntityName === "inspection") return { columns: inspectionColumns }
  if (lowerEntityName === "invoice") return { columns: invoiceColumns }
  if (lowerEntityName === "ledgeraccount") return { columns: ledgerAccountColumns }
  if (lowerEntityName === "ledgerperiod") return { columns: ledgerPeriodColumns }
  if (lowerEntityName === "lesson") return { columns: lessonColumns, fields: lessonFields }
  if (lowerEntityName === "lessoncatalogitem") return { columns: lessonCatalogItemColumns }
  if (lowerEntityName === "maintenance") return { columns: maintenanceColumns, fields: maintenanceFields }
  if (lowerEntityName === "paymentfile") return { columns: paymentFileColumns }
  if (lowerEntityName === "paymentfileline") return { columns: paymentFileLineColumns }
  if (lowerEntityName === "person") return { columns: personColumns }
  if (lowerEntityName === "posting") return { columns: postingColumns }
  if (lowerEntityName === "postingevent") return { columns: postingEventColumns }
  if (lowerEntityName === "privateequityasset") return { columns: privateEquityAssetColumns }
  if (lowerEntityName === "privateequityoperation") return { columns: privateEquityOperationColumns }
  if (lowerEntityName === "product") return { columns: productColumns }
  if (lowerEntityName === "project") return { columns: projectColumns }
  if (lowerEntityName === "prospect") return { columns: prospectColumns }
  if (lowerEntityName === "question") return { columns: questionColumns }
  if (lowerEntityName === "score") return { columns: scoreColumns }
  if (lowerEntityName === "scriptlog") return { columns: scriptLogColumns }
  if (lowerEntityName === "training") return { columns: trainingColumns, fields: trainingFields }
  if (lowerEntityName === "trainingcatalogitem") return { columns: trainingCatalogItemColumns }
  if (lowerEntityName === "user") return { columns: userColumns }

  const entityClientRoute = getEntityClientRoute(entityName) // "AssetLot" => "/asset-lot"
  const entityStatusListName = getEntityStatusListName(entityName) // "AssetLot" => "assetLotStatus"
  return {
    columns: [
      { title: "Registration", name: "registration", linkTo: `${entityClientRoute}/{registration}` },
      { title: "Organization", name: "organization.name", hidden: true },
      { title: "Status", name: "status", select: entityStatusListName, badge: true },
      { title: "Name", name: "name" },
    ],
  }
}

export function getEntityDisplay(entity) {
  if (!entity) return
  if (!entity.name || entity.name === entity.registration) return entity.registration
  if (!entity.registration) return entity.name
  return `${entity.name} (${entity.registration})`
}

export function toFieldNames(fields = []) {
  const fieldNames = fields
    .flat()
    .map(({ field }) => field)
    .filter(field => field)
  return Array.from(new Set(fieldNames))
}

export function getEntityFilterFunction(entityName) {
  if (entityName.toLowerCase() === "asset") return filterAssets
  if (entityName.toLowerCase() === "collateral") return filterCollaterals
  if (entityName.toLowerCase() === "contractlot") return filterContractLots
  if (entityName.toLowerCase() === "postingevent") return filterPostingEvents
  if (entityName.toLowerCase() === "person") return filterPersons
  if (entityName.toLowerCase() === "prospect") return filterProspects
  if (entityName.toLowerCase() === "privateequityasset") return filterPrivateEquityAssets
  if (entityName.toLowerCase() === "question") return filterQuestions
  if (entityName.toLowerCase() === "score") return filterScores
  if (entityName.toLowerCase() === "posting") return filterPostings
  return filterEntities
}

function filterEntities(entities, filter, entityColumns) {
  if (filter) filter = filter.trim().toUpperCase()
  if (!filter) return entities
  const locale = getLocale()
  const includes = getFilterFunction(filter, locale)

  // Filter entities based on "entityColumns"
  if (Array.isArray(entityColumns) && entityColumns.length) {
    return entities.filter(entity => {
      for (let entityColumn of entityColumns) {
        let name
        if (entityColumn?.name) name = entityColumn.name
        else if (typeof entityColumn === "string") name = entityColumn

        if (!name) continue

        let value = get(entity, name)
        // If it's a list check if it matches the label first
        if (entityColumn?.select) {
          const label = getLabel(entityColumn.select, value)
          if (label && includes(label)) return true
        }
        // If it's an object/array then stringify it
        if (entityColumn.type === "object" && (typeof value === "object" || Array.isArray(value))) value = JSON.stringify(value)
        // Match value
        if (includes(value)) return true
      }
      return false
    })
  }
  return entities.filter(entity => includes(entity.registration) || includes(entity.name) || includes(entity.status))
}

function filterPostingEvents(postingEvents, filter) {
  if (filter) filter = filter.trim().toUpperCase()
  if (!filter) return postingEvents

  const locale = getLocale()
  const includes = getFilterFunction(filter, locale)

  return postingEvents.filter(postingEvent => {
    const dcLabel = getLabel("postingDc", postingEvent.dc)
    const codeLabel = getLabel("postingCode", postingEvent.code)
    const typeLabel = getLabel("postingEventType", postingEvent.type)
    const accountLabel = getLabel("ledgerAccount", postingEvent.account)
    const statusLabel = getLabel("postingEventStatus", postingEvent.status)
    const secondaryAccountLabel = getLabel("ledgerAccount", postingEvent.secondaryAccount)

    return (
      includes(dcLabel) ||
      includes(codeLabel) ||
      includes(postingEvent.dc) ||
      includes(statusLabel) ||
      includes(postingEvent.documentReference) ||
      includes(postingEvent.name) ||
      includes(postingEvent.code) ||
      includes(typeLabel) ||
      includes(postingEvent.type) ||
      includes(postingEvent.date) ||
      includes(accountLabel) ||
      includes(postingEvent.amount) ||
      includes(postingEvent.status) ||
      includes(postingEvent.account) ||
      includes(postingEvent.eventType) ||
      includes(postingEvent.description) ||
      includes(postingEvent.registration) ||
      includes(secondaryAccountLabel) ||
      includes(postingEvent.secondaryAccount) ||
      includes(getEntityDisplay(postingEvent)) ||
      includes(postingEvent.personRegistration) ||
      includes(postingEvent.partnerRegistration) ||
      includes(postingEvent.auditTrailReference) ||
      includes(postingEvent.invoiceRegistration) ||
      includes(postingEvent.contractRegistration) ||
      includes(postingEvent.cashflowRegistration) ||
      includes(postingEvent.creditLineRegistration)
    )
  })
}

function filterPrivateEquityAssets(privateEquitys = [], filter) {
  if (!filter) return privateEquitys
  const includes = getFilterFunction(filter, getLocale())

  return privateEquitys.filter(privateEquity => {
    const typeLabel = getLabel("privateEquityAssetType", privateEquity.type)
    const statusLabel = getLabel("privateEquityAssetStatus", privateEquity.status)

    return (
      includes(typeLabel) ||
      includes(statusLabel) ||
      includes(privateEquity.name) ||
      includes(privateEquity.type) ||
      includes(privateEquity.status) ||
      includes(privateEquity.registration) ||
      includes(getEntityDisplay(privateEquity)) ||
      includes(formatAddress(privateEquity.addresses?.[0])) ||
      includes((privateEquity.roles || []).map(r => getLabel("privateEquityAssetRole", r.role) || `${r.role} (???)`).join(", "))
    )
  })
}

function filterQuestions(questions, filter) {
  if (!filter) return questions
  const includes = getFilterFunction(filter, getLocale())

  return questions.filter(question => {
    const typeLabel = getLabel("questionType", question.type)
    const statusLabel = getLabel("questionStatus", question.status)

    return (
      includes(question.type) ||
      includes(typeLabel) ||
      includes(statusLabel) ||
      includes(question.status) ||
      includes(question.registration) ||
      includes(getEntityDisplay(question))
    )
  })
}

function filterScores(scores, filter) {
  if (!filter) return scores
  const includes = getFilterFunction(filter, getLocale())

  return scores.filter(score => {
    const statusLabel = getLabel("scoreStatus", score.status)

    return (
      includes(statusLabel) ||
      includes(score.name) ||
      includes(score.status) ||
      includes(score.registration) ||
      includes(getEntityDisplay(score)) ||
      includes(score.dealRegistration)
    )
  })
}

function filterPostings(postings = [], filter) {
  if (!filter) return postings
  const includes = getFilterFunction(filter, getLocale())

  return postings.filter(posting => {
    const dcLabel = getLabel("postingDc", posting.dc)
    const codeLabel = getLabel("postingCode", posting.code)
    const typeLabel = getLabel("postingType", posting.type)
    const statusLabel = getLabel("postingStatus", posting.status)
    const accountLabel = getLabel("ledgerAccount", posting.account)
    const secondaryAccountLabel = getLabel("ledgerAccount", posting.secondaryAccount)

    return (
      includes(dcLabel) ||
      includes(codeLabel) ||
      includes(posting.dc) ||
      includes(statusLabel) ||
      includes(posting.documentReference) ||
      includes(posting.name) ||
      includes(posting.code) ||
      includes(typeLabel) ||
      includes(posting.type) ||
      includes(posting.date) ||
      includes(accountLabel) ||
      includes(posting.amount) ||
      includes(posting.status) ||
      includes(posting.account) ||
      includes(posting.eventType) ||
      includes(posting.description) ||
      includes(posting.registration) ||
      includes(secondaryAccountLabel) ||
      includes(posting.secondaryAccount) ||
      includes(getEntityDisplay(posting)) ||
      includes(posting.personRegistration) ||
      includes(posting.partnerRegistration) ||
      includes(posting.auditTrailReference) ||
      includes(posting.invoiceRegistration) ||
      includes(posting.contractRegistration) ||
      includes(posting.cashflowRegistration) ||
      includes(posting.creditlineRegistration)
    )
  })
}

function filterPersons(persons = [], filter) {
  if (!filter) return persons
  const includes = getFilterFunction(filter, getLocale())

  return persons.filter(person => {
    const typeLabel = getLabel("personType", person.type)
    const statusLabel = getLabel("personStatus", person.status)
    const rolesLabels = (person.roles || []).map(r => getLabel("personRole", r.role)).join(", ")
    const registrations = (person.registrations || [])
      .map(r => r?.number)
      .filter(it => it)
      .join(", ")

    return (
      includes(typeLabel) ||
      includes(rolesLabels) ||
      includes(person.name) ||
      includes(statusLabel) ||
      includes(person.type) ||
      includes(person.email) ||
      includes(person.status) ||
      includes(person.lastName) ||
      includes(person.firstName) ||
      includes(person.secondName) ||
      includes(person.registration) ||
      includes(getEntityDisplay(person)) ||
      includes(registrations) ||
      includes(formatAddress(person.addresses?.[0]))
    )
  })
}

function filterProspects(prospects = [], filter) {
  if (!filter) return prospects
  const includes = getFilterFunction(filter, getLocale())

  return prospects.filter(prospect => {
    const typeLabel = getLabel("personType", prospect.type) // TODO: Rename to "prospectType"
    const statusLabel = getLabel("personStatus", prospect.status) // TODO: Rename to "prospectStatus"
    const rolesLabels = (prospect.roles || []).map(r => getLabel("personRole", r.role)).join(", ") // TODO: Rename to "prospectRole"
    const registrations = (prospect.registrations || [])
      .map(r => r?.number)
      .filter(it => it)
      .join(", ")

    return (
      includes(typeLabel) ||
      includes(rolesLabels) ||
      includes(prospect.name) ||
      includes(statusLabel) ||
      includes(prospect.type) ||
      includes(prospect.email) ||
      includes(prospect.status) ||
      includes(prospect.lastName) ||
      includes(prospect.firstName) ||
      includes(prospect.secondName) ||
      includes(prospect.registration) ||
      includes(getEntityDisplay(prospect)) ||
      includes(registrations) ||
      includes(formatAddress(prospect.addresses?.[0]))
    )
  })
}

function filterCollaterals(collaterals = [], filter) {
  if (!filter) return collaterals
  const includes = getFilterFunction(filter, getLocale())

  return collaterals.filter(collateral => {
    const typeLabel = getLabel("collateralType", collateral.type)
    const statusLabel = getLabel("collateralStatus", collateral.status)

    return (
      includes(typeLabel) ||
      includes(collateral.name) ||
      includes(collateral.type) ||
      includes(statusLabel) ||
      includes(collateral.status) ||
      includes(collateral.description) ||
      includes(collateral.registration) ||
      includes(getEntityDisplay(collateral))
    )
  })
}

function filterAssets(assets = [], filter) {
  if (!filter) return assets
  const includes = getFilterFunction(filter, getLocale())

  return assets.filter(asset => {
    const typeLabel = getLabel("assetType", asset.type)
    const statusLabel = getLabel("assetStatus", asset.status)

    return (
      includes(typeLabel) ||
      includes(asset.name) ||
      includes(asset.type) ||
      includes(statusLabel) ||
      includes(asset.status) ||
      includes(asset.description) ||
      includes(asset.registration) ||
      includes(getEntityDisplay(asset)) ||
      includes(asset.identificationNumber) ||
      includes(asset.registrationBookNumber) ||
      includes((asset.priceExclTax || 0).toString()) ||
      includes((asset.priceInclTax || 0).toString()) ||
      includes(asset.owner?.name) ||
      includes(asset.supplier?.name)
    )
  })
}

function filterContractLots(contractLots, filter) {
  if (filter) filter = filter.trim().toUpperCase()
  if (!filter) return contractLots

  const locale = getLocale()
  const includes = getFilterFunction(filter, locale)

  return contractLots.filter(contractLot => {
    const typeLabel = getLabel("contractLotType", contractLot.type)
    const statusLabel = getLabel("contractLotStatus", contractLot.status)

    return (
      includes(typeLabel) ||
      includes(statusLabel) ||
      includes(contractLot.code) ||
      includes(contractLot.name) ||
      includes(contractLot.type) ||
      includes(contractLot.status) ||
      includes(contractLot.cessionDate) ||
      includes(contractLot.registration) ||
      includes(contractLot.fundRegistration) ||
      includes(contractLot.externalReference) ||
      includes(contractLot.internalReference) ||
      includes(getEntityDisplay(contractLot)) ||
      includes(formatPercentage(contractLot.rate, locale))
    )
  })
}

export async function handleEntitiesPageRowClick(entityName, component, row) {
  const { selectedEntities } = component.state

  const rowId = row?.id || row?.registration
  if (rowId) {
    if (selectedEntities.has(rowId)) selectedEntities.delete(rowId)
    else selectedEntities.add(rowId)
  }

  if (!selectedEntities.size) return component.setState({ transitions: [], selectedEntities })

  component.setState({ transitionsLoading: true })

  const transitions = await getEntitiesTransitions(entityName, Array.from(selectedEntities))

  component.setState({ transitions, selectedEntities, transitionsLoading: false })
}

export async function handleEntitiesPageSelectAll(entityName, component, selectedEntities) {
  if (!selectedEntities.size) return component.setState({ transitions: [], selectedEntities })

  component.setState({ transitionsLoading: true })

  const transitions = await getEntitiesTransitions(entityName, Array.from(selectedEntities))

  component.setState({ transitions, selectedEntities, transitionsLoading: false })
}

export async function getEntityTransitions(entityName, entityRegistration) {
  try {
    const serverRoute = getEntityServerRoute(entityName)
    const _transitions = (await axios.get(`${serverRoute}/${entityRegistration}/transitions?exclude=modals,printings,documents`)).data

    return _transitions
      .map((tr, index) => {
        // invert pullRight order to respect workflow card order
        tr.orderIndex = tr.props?.pullRight ? 1000 - index : index
        return tr
      })
      .sort((tr1, tr2) => tr1.orderIndex - tr2.orderIndex)
  } catch (error) {
    addOops(error)
  }

  return []
}

async function getEntitiesTransitions(entityName, entityRegistrations) {
  try {
    const serverRoute = getEntityServerRoute(entityName)
    const _transitions = (await axios.post(`${serverRoute}/transitions?exclude=printings,documents`, entityRegistrations)).data

    return _transitions
      .map((tr, index) => {
        // invert pullRight order to respect workflow card order
        tr.orderIndex = tr.props?.pullRight ? 1000 - index : index
        return tr
      })
      .sort((tr1, tr2) => tr1.orderIndex - tr2.orderIndex)
  } catch (error) {
    addOops(error)
  }

  return []
}

export function setEntityFieldProps(entity, fieldProps, { isWorkflowTransitionError } = {}) {
  for (const key in fieldProps) {
    const props = fieldProps[key]
    if (key === "undefined") addOops(`undefined key found in "fieldProps"`)
    else {
      if (isWorkflowTransitionError) props.isWorkflowTransitionError = true
      set(entity, key, props)
    }
  }
}

/**
 * Creates a function for entities based on the configured columns.
 * This function detects if entity rows match hidden columns, allowing a message to be displayed to users to avoid confusing them.
 *
 * @returns {Function} - A function that map entities based on the provided filter, taking into account
 *                       different column types (e.g., currency, percentage, date, bytes and milliseconds)
 *                       and formats the values accordingly before applying the map.
 */
export function getEntityMapFunction(columns) {
  const locale = getLocale()

  return (entities = [], filter, options) => {
    const { isServerSearch } = options || {}
    if (!filter) return { entities }

    let hasSearchResultsOnHiddenFields = false
    const includes = getFilterFunction(filter, locale)
    const mappedEntities = entities.filter(entity => {
      for (const column of columns) {
        const fieldName = column?.filterFunctionField || column?.name || column
        if (!fieldName) {
          if (isServerSearch) {
            hasSearchResultsOnHiddenFields = true
            return true
          }

          continue
        }

        const value = get(entity, fieldName)
        if (!value) {
          if (isServerSearch) {
            hasSearchResultsOnHiddenFields = true
            return true
          }

          continue
        }

        let formattedValue = value
        if (column.select) {
          formattedValue = column.richValuesList
            ? getRichLabel(column.select, value, { richValuesList: column.richValuesList })
            : getLabel(column.select, value)
        } else if (column.type === "currency") {
          if (includes(value)) return true // Support raw numbers also (€1,000 <> 1000)

          let currency = column.currency
          if (!currency && column.currencyPath) currency = get(entity, column.currencyPath)

          formattedValue = formatCurrency(value, locale, { currency })
        } else if (column.type === "percentage") {
          formattedValue = formatPercentage(value, locale)
        } else if (["date", "datetime"].includes(column.type)) {
          if (includes(value)) return true // Support raw dates also
          formattedValue = column.type === "date" ? formatDate(value, locale) : formatDateTime(value, locale)
        } else if (column.type === "bytes") {
          formattedValue = formatBytes(value)
        } else if (column.type === "milliseconds") {
          formattedValue = formatMilliseconds(value)
        }

        if (includes(formattedValue)) return true
      }

      hasSearchResultsOnHiddenFields = isServerSearch
      return isServerSearch
    })

    return { entities: mappedEntities, hasSearchResultsOnHiddenFields }
  }
}
